import {
  CreateEventFormAnswersForm,
  useCreateEventFormAnswersMutation,
} from "@ac-expo/api";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

interface EventFormsContextType {
  formAnswers: Record<string, CreateEventFormAnswersForm>;
  saveFormAnswers: (
    ticketId: string,
    answers: CreateEventFormAnswersForm
  ) => void;
  getFormAnswers: (ticketId: string) => CreateEventFormAnswersForm | undefined;
  clearFormAnswers: (ticketId?: string) => void;
  cleanOrphanedFormAnswers: (validTicketIds: Set<string>) => void;
  submitStoredForms: (consentedEventIds: string[]) => Promise<void>;
  hasStoredAnswers: (ticketId: string) => boolean;
}

const EventFormsContext = createContext<EventFormsContextType | undefined>(
  undefined
);

export function useEventFormsContext() {
  const context = useContext(EventFormsContext);
  if (!context) {
    throw new Error(
      "useEventFormsContext must be used within an EventFormsProvider"
    );
  }
  return context;
}

interface EventFormsProviderProps {
  children: ReactNode;
}

export function EventFormsProvider({ children }: EventFormsProviderProps) {
  const [formAnswers, setFormAnswers] = useState<
    Record<string, CreateEventFormAnswersForm>
  >({});

  const { mutateAsync: createEventFormAnswers } =
    useCreateEventFormAnswersMutation();

  const saveFormAnswers = useCallback(
    (ticketId: string, answers: CreateEventFormAnswersForm) => {
      setFormAnswers((prev) => ({
        ...prev,
        [ticketId]: answers,
      }));
    },
    []
  );

  const getFormAnswers = useCallback(
    (ticketId: string): CreateEventFormAnswersForm | undefined => {
      return formAnswers[ticketId];
    },
    [formAnswers]
  );

  const hasStoredAnswers = useCallback(
    (ticketId: string): boolean => {
      return ticketId in formAnswers;
    },
    [formAnswers]
  );

  const clearFormAnswers = useCallback((ticketId?: string) => {
    if (ticketId) {
      setFormAnswers((prev) => {
        const { [ticketId]: _removed, ...rest } = prev;
        return rest;
      });
    } else {
      setFormAnswers({});
    }
  }, []);

  const cleanOrphanedFormAnswers = useCallback((validTicketIds: Set<string>) => {
    setFormAnswers((prev) => {
      const orphanedKeys = Object.keys(prev).filter(
        (ticketId) => !validTicketIds.has(ticketId)
      );
      if (orphanedKeys.length === 0) return prev;

      const filtered: Record<string, CreateEventFormAnswersForm> = {};
      for (const ticketId of Object.keys(prev)) {
        if (validTicketIds.has(ticketId)) {
          filtered[ticketId] = prev[ticketId];
        }
      }
      return filtered;
    });
  }, []);

  const submitStoredForms = useCallback(
    async (consentedEventIds: string[]): Promise<void> => {
      const ticketIds = Object.keys(formAnswers);
      const submissionPromises = ticketIds
        .filter((ticketId) => consentedEventIds.includes(formAnswers[ticketId].eventId))
        .map((ticketId) => createEventFormAnswers({ data: formAnswers[ticketId] }));

      await Promise.all(submissionPromises);
    },
    [createEventFormAnswers, formAnswers]
  );

  const value: EventFormsContextType = {
    formAnswers,
    saveFormAnswers,
    getFormAnswers,
    clearFormAnswers,
    cleanOrphanedFormAnswers,
    submitStoredForms,
    hasStoredAnswers,
  };

  return (
    <EventFormsContext.Provider value={value}>
      {children}
    </EventFormsContext.Provider>
  );
}
